import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Building2, Search, User } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { inrCompact } from "@/lib/format";
import { TempBadge } from "@/components/common";

export default function GlobalSearch({ open, onOpenChange }) {
  const [q, setQ] = useState("");
  const [dq, setDq] = useState("");
  const nav = useNavigate();

  useEffect(() => {
    const t = setTimeout(() => setDq(q.trim()), 250);
    return () => clearTimeout(t);
  }, [q]);

  const { data, isFetching } = useQuery({
    queryKey: ["search", dq],
    queryFn: async () => (await api.get("/search", { params: { q: dq } })).data,
    enabled: dq.length >= 2,
  });

  const go = (to) => {
    onOpenChange(false);
    setQ("");
    nav(to);
  };
  const empty = dq.length >= 2 && data && !data.leads.length && !data.properties.length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="top-[12%] max-w-lg translate-y-0 gap-0 p-0 sm:rounded-2xl" data-testid="global-search-dialog">
        <DialogTitle className="sr-only">Search</DialogTitle>
        <div className="flex items-center gap-2 border-b px-4">
          <Search className="h-4 w-4 text-muted-foreground" />
          <input
            autoFocus
            data-testid="global-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Name, phone, project, sector…"
            className="h-14 flex-1 bg-transparent text-sm outline-none"
          />
        </div>
        <div className="max-h-[60vh] overflow-y-auto p-2">
          {dq.length < 2 && <p className="px-3 py-6 text-center text-sm text-muted-foreground">Type at least 2 characters</p>}
          {isFetching && <p className="px-3 py-2 text-xs text-muted-foreground">Searching…</p>}
          {empty && <p className="px-3 py-6 text-center text-sm text-muted-foreground" data-testid="global-search-empty">No results</p>}
          {data?.leads?.length > 0 && <p className="label-xs px-3 pb-1 pt-2">Leads</p>}
          {data?.leads?.map((l) => (
            <button key={l.id} data-testid={`search-result-lead-${l.id}`} onClick={() => go(`/leads/${l.id}`)} className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left hover:bg-secondary">
              <User className="h-4 w-4 text-muted-foreground" />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium">{l.name}</span>
                <span className="text-xs text-muted-foreground">{l.phone} · {l.stage}</span>
              </span>
              <TempBadge t={l.temperature} />
            </button>
          ))}
          {data?.properties?.length > 0 && <p className="label-xs px-3 pb-1 pt-3">Properties</p>}
          {data?.properties?.map((p) => (
            <button key={p.id} data-testid={`search-result-property-${p.id}`} onClick={() => go(`/properties/${p.id}`)} className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left hover:bg-secondary">
              <Building2 className="h-4 w-4 text-muted-foreground" />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium">{p.project || p.property_type}</span>
                <span className="text-xs text-muted-foreground">{p.property_type} · {[p.sector, p.city].filter(Boolean).join(", ")}</span>
              </span>
              <span className="num text-xs">{inrCompact(p.price)}</span>
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
